<template>
  <div class="product-price-wrapper large">
    <DetailsModal
      v-if="showModal"
      :name="modalName"
      :badges-list="price.badgesList"
      @close="closeModal"
    />
    <div
      v-if="showPriceType"
      class="price-type-container label"
      :class="{ 'flex-container': showPriceType }"
    >
      <div
        v-if="price.priceType?.onEdv"
        class="edv-bdg"
      />
      <span>{{ price.priceType?.text }}</span>
      <div
        v-if="price.flags.isLastAct"
        class="last-act-wrapper"
      >
        <span class="label">: Final Sale</span>
        <button
          type="button"
          aria-label="Toggletip"
          class="help-button"
          @click="toggletip.toggle"
        />
        <Toggletip
          id="last-act-toggletip-mcom"
          ref="toggletip"
          position="top"
          :width="327"
        >
          <div id="final-sale-tooltip-wrapper">
            <div id="final-sale-tooltip">
              <p>
                Last Act items are final sale and cannot be returned. Extra savings do not apply.
                <br>
                <a
                  href="https://customerservice-macys.com/articles/what-is-macys-return-policy#Last-Act-Merchandise"
                  target="_blank"
                  class="details-link"
                >
                  Details
                </a>
              </p>
            </div>
            <div class="final-sale-tooltip-icon tooltip-large" />
          </div>
        </Toggletip>
      </div>
    </div>
    <div
      :class="{ 'promo': isPromo }"
      class="price-wrapper"
    >
      <span>
        <span
          class="price-lg"
          :class="{ 'price-red': price.flags.isLowestPriceRed }"
        >
          <span v-if="price.flags.isSaleLabel"> {{ price.lowestSalePriceTier.label + " " }} </span>
          <span
            role="text"
            tabindex="0"
            :aria-label="'Current Price: ' + (price.lowestSalePriceTier?.values && price.lowestSalePriceTier?.values[0]?.formattedValue)"
          > {{ price.lowestSalePriceTier?.values && price.lowestSalePriceTier?.values[0]?.formattedValue }}  </span>
        </span>
        <span v-if="price.flags.isWithOfferPromo"> with code: {{ price.lowestSalePriceTier.promoCode }}  </span>
      </span>
      <span
        v-if="price.flags.isShowRegularPrice"
        class="price-strike-lg"
        role="text"
        tabindex="0"
        :aria-label="'Previous Price: ' + (price.regularPriceTier?.values && price.regularPriceTier?.values[0]?.formattedValue )"
      >
        {{ price.regularPriceTier?.values && price.regularPriceTier?.values[0]?.formattedValue }}
      </span>

      <span v-if="!price.flags.isLastAct && price.promoCodeHeader">
        {{ price.promoCodeHeader }}
      </span>
      <span
        v-if="isPromoCodeRegularPrice"
        class="price-strike-lg"
      >
        {{ price.regularPriceTier?.values?.[0]?.formattedValue }}
      </span>
      <span v-if="price.priceType?.giftPriceLabelToAllUpcs">
        ({{ price.giftpriceLabelFormatted }})
      </span>
      <button
        class="link-sm"
        data-testid="details-link"
        @click="toggleDetailsModal"
      >
        Details
      </button>
      <div
        v-if="additionalFeeInfo.bigTicketFees || additionalFeeInfo.smallTicketFees"
        class="additional-fees medium"
      >
        <div v-if="additionalFeeInfo.bigTicketFees">
          <span>{{ additionalFeeInfo.formattedPrice }} with </span>
          <span v-if="!additionalFeeInfo.isParcelShipping">scheduled</span> delivery
        </div>
        <div v-else>
          {{ additionalFeeInfo.formattedPrice }} with delivery
        </div>
      </div>
      <div
        v-if="price.flags.creditFinance && !isProductPerksEnabled"
      >
        <Ccfp
          :price="price"
        />
        <Klarna
          v-if="price.flags.klarna"
          :price="price"
        />
      </div>

      <div
        v-if="price?.offers?.regularOffer && !isProductPerksEnabled"
        class="secondary-promo"
      >
        <button
          class="link-sm secondary-promo"
          :class="{ 'margin-top-xs' : !isSpacingEnabled }"
          @click="toggleDetailsModal"
        >
          <span>{{ price.offers.regularOffer.header }}</span>
          <span v-if="price.offers.isMultipleOffers">  + More</span>
        </button>
      </div>
      <div
        v-if="price.fauxOffer && isProductPerksEnabled"
        class="secondary-promo"
      >
        <button
          class="link-sm secondary-promo"
          :class="{ 'margin-top-xs' : !isSpacingEnabled }"
          @click="toggleDetailsModal"
        >
          <span>{{ price.fauxOffer.header }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed, ref, defineAsyncComponent, provide,
} from 'vue';
import Toggletip from '@atomic-ui/toggletip';
import useModal from '../../../composables/useModal';
import { PRICE_OVERLAY_DATA } from './constants';
import { PRICE_OVERLAY_DATA as PRICE_OVERLAY_DATA_BCOM } from './bcom/constants';
import Ccfp from '../../credit-finance/components/Ccfp';
import Klarna from '../../credit-finance/components/Klarna';
import formatCurrency from '../../../utils/currencyFormatter';
import { fireDetailsAnalytics } from './utils.common';
import { useBrand } from '../../../composables/useBrand';

const DetailsModal = defineAsyncComponent(() => import(/* webpackChunkName: 'DetailsModal' */ './DetailsModal'));

const props = defineProps({
  name: {
    type: String,
    default: 'price-section',
  },
  price: {
    type: Object,
    default: null,
  },
  additionalFee: {
    type: Object,
    default: null,
  },
  isSpacingEnabled: {
    type: Boolean,
    default: false,
  },
  isProductPerksEnabled: {
    type: Boolean,
    default: false,
  },
});

const { isBcom } = useBrand();
const priceOverlayData = computed(() => props.price);
provide(isBcom ? PRICE_OVERLAY_DATA_BCOM : PRICE_OVERLAY_DATA, priceOverlayData);

const emit = defineEmits(['open-details-modal', 'close-details-modal']);

const { open } = useModal();

const toggletip = ref(null);

const isPromoCodeRegularPrice = computed(() => !(props.price.flags.isShowRegularPrice && props.price.flags.isWithOfferPromo)
  && (props.price.promoCodeHeader && !props.price.flags.isLastAct));
const isPromo = computed(() => props.price?.promoCodeHeader || props.price?.flags.isWithOfferPromo);
const showPriceType = computed(() => props.price.flags?.isLastAct || props.price.priceType?.onEdv || props.price.priceType?.text);
const additionalFeeInfo = computed(() => {
  const deliveryMethod = props.additionalFee?.deliveryMethod;
  const isParcelShipping = deliveryMethod?.type === '5' || deliveryMethod?.type === '6';
  const bigTicketFees = props.additionalFee?.deliveryMethod?.totalDeliveryFee;
  const smallTicketFees = props.additionalFee?.surchargeAmount;

  const lowestSalePriceTier = props.price.lowestSalePriceTier?.values?.[0]?.value;
  const additionalFee = bigTicketFees || smallTicketFees;
  const formattedPrice = formatCurrency(lowestSalePriceTier + additionalFee);

  return {
    bigTicketFees: props.additionalFee?.deliveryMethod?.totalDeliveryFee,
    smallTicketFees: props.additionalFee?.surchargeAmount,
    isParcelShipping,
    formattedPrice,
  };
});

const modalName = `${props.name}-detail-modal`;
const showModal = ref(false);

const toggleDetailsModal = () => {
  fireDetailsAnalytics({
    id: props.price.id,
    value: String(props.price.offers?.lists?.activeOffers?.length ?? 0),
  });
  showModal.value = true;
  open(modalName);
  emit('open-details-modal');
};

const closeModal = () => {
  showModal.value = false;
  emit('close-details-modal');
};
</script>

<style lang="scss" scoped>
@import "~settingSCSS";
@import "~@csgIcons/_tooltip-small.scss";
@import "~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss";

.details-link {
  display: block;
  @include margin-direction('top', map-get($spacer, 'xxs'));
  color: $white;
}

.last-act-wrapper {
  display: flex;
  align-items: center;
}

.help-button {
  cursor: pointer;
  @extend .tooltip-small;
}

.edv-bdg:before {
  content: "edv";
  letter-spacing: -.125rem;
  font-family: monospace;
  font-weight: 700;
  font-size: 1rem;
  color: $black;
  text-transform: lowercase;
  @include padding-direction('right', map-get($spacer, 'xxs'));
}

.price-red {
  color: $sale-color
}

.price-wrapper>span {
  @include padding-direction('right', map-get($spacer, 'xxs'));
}

.price-type-container {
  text-transform: uppercase;
  margin-bottom: 4px;
}

.price-type-container.flex-container {
  display: flex;
  align-items: baseline;
}

.secondary-promo {
  display: block;
  width: fit-content;
}

.additional-fees {
  font-weight: $font-weight-sm;
  margin-top: rem-calc(3.5);
}
</style>
